import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import {
  calculateCartItem,
  deepCopyFunction,
  findPricingViaIdHelper,
  truncateString,
} from "../../../helpers";
import { useParams } from "react-router-dom";
import { ToastContext } from "../../../hooks/context";
import { useOtpAuthTrigger } from "../../../hooks/useOtpAuthTrigger";

const useProductCarting = ({
  custom_price,
  addCartItemToLocalStorage,
  product,
  pricing_type_id,
  cartIndex,
  manual_discount,
}) => {
  const { store_id } = useParams();
  const [tagIsActive, setTagIsActive] = useState({
    isPricing: false,
    isUnit: false,
    isDiscount: false,
  });
  const [customValue, setCustomValue] = useState(custom_price || "");
  const [discountType, setDiscountType] = useState(
    manual_discount?.type || "flat",
  );
  const [discountValue, setDiscountValue] = useState(
    manual_discount?.amount || "",
  );
  const { data: cartItems, ...otherCartInfo } = useSelector(
    (state) => state.cartReducer.cartDetails,
  );
  const { storeList } = useSelector((state) => state.profileDetailsReducer);

  const storeProperties = storeList?.find(
    (item) => `${item?.id}` === `${store_id}`,
  );
  const triggerToast = useContext(ToastContext);
  const [pricingTypeId, setPricingTypeId] = useState(pricing_type_id);
  const { handleOrderModuleAuth, isLoadingAuthAction } = useOtpAuthTrigger({
    storeId: store_id,
    params: {},
  });

  const addCustomPricing = (position) => {
    if (!storeProperties?.allow_custom_price)
      return triggerToast(
        "Activate this feature in your store settings or contact your store admin for assistance",
        "warning",
      );

    const newCartItems = deepCopyFunction(cartItems);
    newCartItems[position].custom_price = Number(customValue);
    newCartItems[position].total_price =
      Number(customValue) * newCartItems[position]?.quantity;

    const data = calculateCartItem({
      cart_items: newCartItems,
      ...otherCartInfo,
    });
    addCartItemToLocalStorage({ ...data });
    triggerToast(
      `Custom Pricing set for item: (${truncateString(
        newCartItems?.[position]?.cart_item_name,
        40,
      )})`,
      "success",
    );
    setTagIsActive(false);
  };

  const changePricingType = (position, pricingTypeId) => {
    const pricingDetails = findPricingViaIdHelper(pricingTypeId, pricings());
    const unitPrice = pricingDetails?.unit_price;

    const newCartItems = deepCopyFunction(cartItems);
    newCartItems[position].pricing_type_id = pricingTypeId;
    newCartItems[position].total_price = (
      unitPrice * Number(newCartItems[position]?.quantity)
    )?.toFixed(2);
    newCartItems[position].unit_price = unitPrice;
    newCartItems[position].discount_rate = pricingDetails?.discount_rate;
    // newCartItems[position].total_price_without_discount = (
    //   pricingDetails?.price * Number(newCartItems[position]?.quantity)
    // )?.toFixed(2);
    delete newCartItems?.[position]?.custom_price;
    const data = calculateCartItem({
      cart_items: newCartItems,
      ...otherCartInfo,
    });
    addCartItemToLocalStorage({ ...data });
  };

  const updateUnit = (position, unitId, equivalentValue, unit) => {
    const newCartItems = deepCopyFunction(cartItems);
    newCartItems[position].unit = {
      id: unitId,
      value: equivalentValue,
      unit,
    };
    const data = calculateCartItem({
      cart_items: newCartItems,
      ...otherCartInfo,
    });
    addCartItemToLocalStorage({ ...data });
  };

  const pricings = () => {
    let pricings = null;
    if (product?.variant) {
      pricings = product?.variant?.store_variant_properties?.[0]?.pricings;
    } else {
      pricings =
        product?.[
          product?.is_service
            ? "store_service_properties"
            : "store_product_properties"
        ]?.[0]?.pricings;
    }
    return pricings;
  };

  const resetTagView = (key, value = true) => {
    setTagIsActive((prev) => ({
      isPricing: false,
      isUnit: false,
      isDiscount: false,
      [key]: value,
    }));
  };

  const addLineDiscount = (position) => {
    const item = cartItems?.[position];
    if (!item) return;

    const basePrice = item?.custom_price
      ? Number(item.custom_price)
      : Number(item?.inputed_selling_price || item?.unit_price || 0);

    const val = Number(discountValue);

    if (basePrice <= 0) {
      return triggerToast(
        "Discount can not be applied to an item priced at zero",
        "warning",
      );
    }

    if (!discountValue || isNaN(val) || val <= 0) {
      return triggerToast("Please enter a valid discount amount", "warning");
    }

    if (discountType === "percentage" && val > 100) {
      return triggerToast(
        "Percentage discount cannot be greater than 100%",
        "warning",
      );
    }

    if (discountType === "flat" && val > basePrice) {
      return triggerToast(
        "Discount amount can not be more than the item price",
        "warning",
      );
    }

    handleOrderModuleAuth({
      actionKey: "add_discount_to_order",
      onSuccessCallBack: () => {
        const discountAmt =
          discountType === "percentage" ? (val / 100) * basePrice : val;

        const lineUnitPrice = Math.max(0, basePrice - discountAmt);

        const newCartItems = deepCopyFunction(cartItems);
        newCartItems[position].manual_discount = {
          type: discountType,
          amount: val,
        };
        newCartItems[position].unit_price = lineUnitPrice;
        newCartItems[position].total_price =
          lineUnitPrice * Number(newCartItems[position]?.quantity);
        newCartItems[position].discount_rate =
          discountType === "percentage" ? val : 0;

        const data = calculateCartItem({
          cart_items: newCartItems,
          ...otherCartInfo,
        });
        addCartItemToLocalStorage({ ...data });
        triggerToast(
          `Discount applied for item: (${truncateString(
            newCartItems?.[position]?.cart_item_name,
            40,
          )})`,
          "success",
        );
        setTagIsActive((prev) => ({ ...prev, isDiscount: false }));
      },
    });
  };

  const removeLineDiscount = (position) => {
    const newCartItems = deepCopyFunction(cartItems);
    const item = newCartItems?.[position];
    if (!item) return;

    const pricingList = item?.product?.variant
      ? item?.product?.variant?.store_variant_properties?.[0]?.pricings
      : item?.product?.[
          item?.is_service
            ? "store_service_properties"
            : "store_product_properties"
        ]?.[0]?.pricings;

    const pricingDetails = findPricingViaIdHelper(
      item?.pricing_type_id,
      pricingList,
    );
    const originalUnitPrice = item?.custom_price
      ? Number(item.custom_price)
      : Number(pricingDetails?.unit_price || item?.inputed_selling_price || 0);

    delete newCartItems[position].manual_discount;
    newCartItems[position].unit_price = originalUnitPrice;
    newCartItems[position].total_price =
      originalUnitPrice * Number(newCartItems[position]?.quantity);
    newCartItems[position].discount_rate = pricingDetails?.discount_rate || 0;

    const data = calculateCartItem({
      cart_items: newCartItems,
      ...otherCartInfo,
    });
    addCartItemToLocalStorage({ ...data });
    setDiscountValue("");
    triggerToast(
      `Discount removed for item: (${truncateString(
        item?.cart_item_name,
        40,
      )})`,
      "success",
    );
    setTagIsActive((prev) => ({ ...prev, isDiscount: false }));
  };

  const checkMinimumCustomPricing = (unit_price) => {
    const minimumCustomRate = storeProperties?.minimum_custom_price_rate ?? 0;
    const minimumCustomPrice =
      minimumCustomRate === 0
        ? 0
        : unit_price - (unit_price * minimumCustomRate) / 100;

    return {
      unitPrice: +unit_price.toFixed(2),
      minimumCustomPrice: +minimumCustomPrice.toFixed(2),
    };
  };

  return {
    tagIsActive,
    customValue,
    pricingTypeId,
    discountType,
    discountValue,
    isLoadingAuthAction,
    triggerToast,
    storeProperties,
    resetTagView,
    pricings,
    setPricingTypeId,
    setCustomValue,
    setDiscountType,
    setDiscountValue,
    setTagIsActive,
    addCustomPricing,
    addLineDiscount,
    removeLineDiscount,
    changePricingType,
    handleOrderModuleAuth,
    updateUnit,
    checkMinimumCustomPricing,
  };
};
export default useProductCarting;
